import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Check, X, Clock, ArrowLeftRight, CalendarPlus, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../utils/logger';

const SERVICE_LABELS = { day_program: 'Explore Program', in_home: 'In-Home & Community', employment: 'Employment' };
const SERVICE_STYLES = {
  day_program: 'service-day-program',
  in_home: 'service-in-home',
  employment: 'service-employment',
};
const STATUS_STYLES = {
  pending: 'bg-[#FDF0ED] text-[#F4A261] border border-[#F4A261]',
  approved: 'bg-green-50 text-[#2E7D32] border border-green-200',
  denied: 'bg-red-50 text-[#D9534F] border border-red-200',
  cancelled: 'bg-gray-50 text-gray-500 border border-gray-200',
};
const TO_TYPE_LABELS = { pto: 'PTO', sick: 'Sick', unpaid: 'Unpaid', other: 'Other' };

export default function ShiftRequests() {
  const { api, user } = useAuth();
  const role = user?.role || 'staff';
  const isAdmin = role === 'administrator' || role === 'supervisor';
  const canEnterForStaff = isAdmin || role === 'scheduler';
  const [requests, setRequests] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [section, setSection] = useState('shifts');
  const [showEnter, setShowEnter] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [toForm, setToForm] = useState({ user_id: '', type: 'pto', start_date: '', end_date: '', reason: '' });
  const [savingTo, setSavingTo] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get('/shift-requests'),
        api.get('/time-off'),
      ]);
      setRequests(r1.data || []);
      setTimeOff(r2.data || []);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openEnter = async () => {
    setShowEnter(true);
    if (employees.length === 0) {
      try { const { data } = await api.get('/employees'); setEmployees(data || []); } catch (e) { logger.error(e); }
    }
  };

  const submitTimeOff = async () => {
    if (!toForm.user_id || !toForm.start_date || !toForm.end_date) { toast.error('Pick a staff member and dates'); return; }
    setSavingTo(true);
    try {
      const { data } = await api.post('/time-off', toForm);
      toast.success(`Time off entered for ${data.user_name} — auto-approved`);
      setShowEnter(false);
      setToForm({ user_id: '', type: 'pto', start_date: '', end_date: '', reason: '' });
      setSection('timeoff'); setActiveTab('approved');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSavingTo(false);
  };

  const handleAction = async (requestId, action) => {
    try { await api.put(`/shift-requests/${requestId}?action=${action}`); fetchAll(); }
    catch (e) { logger.error(e); toast.error('Error'); }
  };

  const handleTimeOffAction = async (id, status) => {
    try {
      await api.put(`/time-off/${id}/status`, { status });
      toast.success(`Request ${status}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteTimeOff = async (id) => {
    if (!window.confirm('Remove this time-off request?')) return;
    try { await api.delete(`/time-off/${id}`); toast.success('Removed'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const filteredRequests = requests.filter(r => r.status === activeTab);
  const filteredTimeOff = timeOff.filter(t => activeTab === 'pending' ? t.status === 'pending' : activeTab === 'approved' ? t.status === 'approved' : t.status === 'denied');

  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="shift-requests-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl tracking-tight font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Requests
          </h1>
          <p className="text-base text-[#5A4D6E] mt-1">
            {isAdmin ? 'Manage shift changes and time-off requests' : 'Your submitted requests'}
          </p>
        </div>
        {canEnterForStaff && (
          <Button onClick={openEnter} className="bg-[#2D1B4E] hover:bg-[#3D2A63] text-white gap-1.5" data-testid="enter-timeoff-btn">
            <UserPlus className="h-4 w-4" /> Enter time off for staff
          </Button>
        )}
      </div>

      <Tabs value={section} onValueChange={setSection}>
        <TabsList className="bg-[#F0EDF6] border border-[#DED8EB]" data-testid="request-section-tabs">
          <TabsTrigger value="shifts" className="data-[state=active]:bg-white gap-1.5" data-testid="section-shifts">
            <ArrowLeftRight className="h-3.5 w-3.5" /> Shift Changes ({requests.length})
          </TabsTrigger>
          <TabsTrigger value="timeoff" className="data-[state=active]:bg-white gap-1.5" data-testid="section-timeoff">
            <CalendarPlus className="h-3.5 w-3.5" /> Time Off ({timeOff.length})
          </TabsTrigger>
        </TabsList>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="bg-[#F0EDF6] border border-[#DED8EB]" data-testid="request-tabs">
            <TabsTrigger value="pending" className="data-[state=active]:bg-white data-[state=active]:text-[#2D1B4E]" data-testid="tab-pending">
              Pending ({section === 'shifts' ? requests.filter(r => r.status === 'pending').length : timeOff.filter(t => t.status === 'pending').length})
            </TabsTrigger>
            <TabsTrigger value="approved" className="data-[state=active]:bg-white data-[state=active]:text-[#2E7D32]" data-testid="tab-approved">
              Approved ({section === 'shifts' ? requests.filter(r => r.status === 'approved').length : timeOff.filter(t => t.status === 'approved').length})
            </TabsTrigger>
            <TabsTrigger value="denied" className="data-[state=active]:bg-white data-[state=active]:text-[#D9534F]" data-testid="tab-denied">
              {section === 'shifts' ? 'Denied' : 'Denied'} ({section === 'shifts' ? requests.filter(r => r.status === 'denied').length : timeOff.filter(t => t.status === 'denied').length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4 space-y-3">
            {loading ? (
              <div className="text-center py-12 text-[#8E85A0]">Loading...</div>
            ) : section === 'shifts' ? (
              filteredRequests.length === 0 ? (
                <EmptyState text={`No ${activeTab} shift requests`} icon={ArrowLeftRight} />
              ) : filteredRequests.map(req => (
                <Card key={req.id} className="border-[#DED8EB] shadow-sm bg-white hover:-translate-y-px transition-all duration-200" data-testid={`request-${req.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[#2D1B4E]">{req.requester_name}</span>
                          <Badge className={`text-xs ${STATUS_STYLES[req.status]}`}>{req.status}</Badge>
                          <Badge className="text-xs bg-[#F0EDF6] text-[#5A4D6E] border border-[#DED8EB]">
                            {req.request_type === 'swap' ? 'Swap' : req.request_type === 'claim' ? 'Claim Open Shift' : 'Cover'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-[#5A4D6E]">
                          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{req.shift_date} | {req.shift_time}</span>
                          {req.service_line && (
                            <Badge className={`text-xs ${SERVICE_STYLES[req.service_line]}`}>
                              {SERVICE_LABELS[req.service_line] || req.service_line}
                            </Badge>
                          )}
                        </div>
                        {req.reason && <p className="text-sm text-[#8E85A0] italic">"{req.reason}"</p>}
                      </div>
                      {isAdmin && req.status === 'pending' && (
                        <div className="flex items-center gap-2 shrink-0">
                          <Button size="sm" onClick={() => handleAction(req.id, 'approve')} className="bg-[#2E7D32] hover:bg-[#236B27] text-white gap-1" data-testid={`approve-${req.id}`}>
                            <Check className="h-3.5 w-3.5" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleAction(req.id, 'deny')} className="border-[#D9534F] text-[#D9534F] hover:bg-red-50 gap-1" data-testid={`deny-${req.id}`}>
                            <X className="h-3.5 w-3.5" /> Deny
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : filteredTimeOff.length === 0 ? (
              <EmptyState text={`No ${activeTab} time-off requests`} icon={CalendarPlus} />
            ) : filteredTimeOff.map(to => (
              <Card key={to.id} className="border-[#DED8EB] shadow-sm bg-white hover:-translate-y-px transition-all duration-200" data-testid={`time-off-req-${to.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#2D1B4E]">{to.user_name}</span>
                        <Badge className={`text-xs ${STATUS_STYLES[to.status]}`}>{to.status}</Badge>
                        <Badge className="text-xs bg-[#F0EDF6] text-[#5A4D6E] border border-[#DED8EB]">
                          {TO_TYPE_LABELS[to.type] || to.type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-[#5A4D6E]">
                        <span className="flex items-center gap-1" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          <Clock className="h-3.5 w-3.5" />{to.start_date}{to.end_date !== to.start_date ? ` → ${to.end_date}` : ''}
                        </span>
                      </div>
                      {to.reason && <p className="text-sm text-[#8E85A0] italic">"{to.reason}"</p>}
                      {to.reviewed_by && <p className="text-[10px] text-[#8E85A0]">Reviewed by {to.reviewed_by}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isAdmin && to.status === 'pending' && (
                        <>
                          <Button size="sm" onClick={() => handleTimeOffAction(to.id, 'approved')} className="bg-[#2E7D32] hover:bg-[#236B27] text-white gap-1" data-testid={`approve-to-${to.id}`}>
                            <Check className="h-3.5 w-3.5" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleTimeOffAction(to.id, 'denied')} className="border-[#D9534F] text-[#D9534F] hover:bg-red-50 gap-1" data-testid={`deny-to-${to.id}`}>
                            <X className="h-3.5 w-3.5" /> Deny
                          </Button>
                        </>
                      )}
                      {isAdmin && to.status !== 'pending' && (
                        <Button size="icon" variant="ghost" onClick={() => deleteTimeOff(to.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`delete-to-${to.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {!isAdmin && to.status === 'pending' && to.user_id === user?.id && (
                        <Button size="sm" variant="outline" onClick={() => deleteTimeOff(to.id)} className="border-[#DED8EB] text-[#8E85A0] gap-1" data-testid={`cancel-to-${to.id}`}>
                          <X className="h-3.5 w-3.5" /> Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </Tabs>

      <Dialog open={showEnter} onOpenChange={setShowEnter}>
        <DialogContent className="max-w-md" data-testid="enter-timeoff-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#2D1B4E]"><CalendarPlus className="h-4 w-4" /> Enter time off for staff</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#8E85A0]">Entered on their behalf — approved instantly. Scheduling tools will avoid these days.</p>
            <div>
              <label className="text-xs font-bold text-[#5A4D6E]">Staff member</label>
              <select value={toForm.user_id} onChange={e => setToForm(f => ({ ...f, user_id: e.target.value }))}
                      className="w-full h-10 px-3 rounded-md border border-[#DED8EB] text-sm bg-white" data-testid="enter-timeoff-staff">
                <option value="">Select staff…</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-[#5A4D6E]">Type</label>
              <div className="flex gap-1.5 flex-wrap mt-1">
                {Object.entries(TO_TYPE_LABELS).map(([k, v]) => (
                  <button key={k} onClick={() => setToForm(f => ({ ...f, type: k }))}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold border ${toForm.type === k ? 'bg-[#2D1B4E] text-white border-[#2D1B4E]' : 'text-[#5A4D6E] border-[#DED8EB]'}`}
                          data-testid={`enter-timeoff-type-${k}`}>{v}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold text-[#5A4D6E]">From</label>
                <Input type="date" value={toForm.start_date}
                       onChange={e => setToForm(f => ({ ...f, start_date: e.target.value, end_date: f.end_date || e.target.value }))}
                       data-testid="enter-timeoff-start" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#5A4D6E]">To</label>
                <Input type="date" value={toForm.end_date} min={toForm.start_date}
                       onChange={e => setToForm(f => ({ ...f, end_date: e.target.value }))}
                       data-testid="enter-timeoff-end" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-[#5A4D6E]">Reason (optional)</label>
              <Input value={toForm.reason} placeholder="e.g. family vacation"
                     onChange={e => setToForm(f => ({ ...f, reason: e.target.value }))} data-testid="enter-timeoff-reason" />
            </div>
            <Button onClick={submitTimeOff} disabled={savingTo} className="w-full bg-[#2D1B4E] hover:bg-[#3D2A63] text-white" data-testid="enter-timeoff-submit">
              {savingTo ? 'Saving…' : 'Save time off (auto-approved)'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ text, icon: Icon }) {
  return (
    <Card className="border-[#DED8EB] shadow-sm bg-white">
      <CardContent className="py-12 text-center">
        <Icon className="h-10 w-10 mx-auto mb-3 text-[#8E85A0] opacity-50" />
        <p className="text-[#8E85A0]">{text}</p>
      </CardContent>
    </Card>
  );
}
